import type { CollectionSortOrder } from "@/features/collections/CollectionSortOrder";
import type { ProgressSummarySortOrder } from "@/features/collections/ProgressSummarySortOrder";
import type { PublicTransactionSummariesSortOrder } from "@/features/collections/PublicTransactionSummariesSortOrder";
import type { TransactionDetailsSortOrder } from "@/features/collections/TransactionDetailsSortOrder";
import type { TransactionSummariesSortOrder } from "@/features/collections/TransactionSummariesSortOrder";
import type { EvCategoryVariantSort } from "@/features/ev/EvCategoryVariantSort";
import type { EvListSort } from "@/features/ev/EvListSort";
import type { EvProductType } from "@/features/ev/EvProductType";
import type { EvSetType } from "@/features/ev/EvSetType";
import type { PerformanceDeltaSort } from "@/features/performanceDeltas/PerformanceDeltaSort";
import type { PerformanceDeltaStep } from "@/features/performanceDeltas/PerformanceDeltaStep";
import type { ViewType } from "@/lib/common/ViewType";
import type { ChartHistoryDuration } from "@/lib/domain/ChartHistoryDuration";
import type { Currency } from "@/lib/domain/Currency";
import type { EmblemSetType } from "@/lib/domain/EmblemSet";
import type { PriceSource } from "@/lib/domain/PriceSource";
import { createGlobalReducerState } from "@/lib/state/createGlobalReducerState";
import { useStorage, type StorageService } from "@/lib/storage";
import { useCallback } from "react";

export interface UserPreferencesState {
	viewTypePreference: ViewType;
	performanceDeltaStep: PerformanceDeltaStep;
	performanceDeltaMinimum: number;
	performanceDeltaSort: PerformanceDeltaSort;
	performanceDeltaSearchQuery: string;
	evListSort: EvListSort;
	evProductType: EvProductType;
	evExcludedCategories: Partial<Record<EvSetType, string[]>>;
	evCategoryVariantSort: EvCategoryVariantSort;
	chartHistoryDuration: ChartHistoryDuration;
	collectionSortOrder: CollectionSortOrder;
	collectionFilterQuery: string;
	collectionSummaryTransactionSortOrder: TransactionSummariesSortOrder;
	collectionSummaryProgressSortOrder: ProgressSummarySortOrder;
	transactionDetailsSortOrder: TransactionDetailsSortOrder;
	emblemSet: EmblemSetType;
	publicCollectionCurrency: Currency | null;
	publicCollectionPriceSource: PriceSource | null;
	publicTransactionSummariesSortOrder: PublicTransactionSummariesSortOrder;
}

type UserPreferencesAction = {
	type: "SetViewTypePreference";
	viewTypePreference: ViewType;
} | {
	type: "SetPerformanceDeltaStep";
	step: PerformanceDeltaStep;
} | {
	type: "SetPerformanceDeltaMinimum";
	minimum: number;
} | {
	type: "SetPerformanceDeltaSort";
	sort: PerformanceDeltaSort;
} | {
	type: "SetPerformanceDeltaSearchQuery";
	performanceDeltaSearchQuery: string;
} | {
	type: "SetEvListSort";
	sort: EvListSort;
} | {
	type: "SetEvProductType";
	productType: EvProductType;
} | {
	type: "SetEvExcludedCategories";
	evExcludedCategories: Partial<Record<EvSetType, string[]>>;
} | {
	type: "SetEvCategoryVariantSort";
	sort: EvCategoryVariantSort;
} | {
	type: "SetChartHistoryDuration";
	chartHistoryDuration: ChartHistoryDuration;
} | {
	type: "SetCollectionSortOrder";
	collectionSortOrder: CollectionSortOrder;
} | {
	type: "SetCollectionFilterQuery";
	collectionFilterQuery: string;
} | {
	type: "SetCollectionSummaryTransactionSortOrder";
	collectionSummaryTransactionSortOrder: TransactionSummariesSortOrder;
} | {
	type: "SetCollectionSummaryProgressSortOrder";
	collectionSummaryProgressSortOrder: ProgressSummarySortOrder;
} | {
	type: "SetTransactionDetailsSortOrder";
	transactionDetailsSortOrder: TransactionDetailsSortOrder;
} | {
	type: "SetEmblemSet";
	emblemSet: EmblemSetType;
} | {
	type: "SetPublicCollectionCurrency";
	publicCollectionCurrency: Currency;
} | {
	type: "SetPublicCollectionPriceSource";
	publicCollectionPriceSource: PriceSource;
} | {
	type: "SetPublicTransactionSummariesSortOrder";
	publicTransactionSummariesSortOrder: PublicTransactionSummariesSortOrder;
};

const getInitialState = (storage: StorageService): UserPreferencesState => {
	const viewTypePreference = storage.getPersistent<ViewType>("viewTypePreference");
	const performanceDeltaStep = storage.getPersistent<PerformanceDeltaStep>("performanceDeltaStep");
	const performanceDeltaMinimum = storage.getPersistent<number>("performanceDeltaMinimum");
	const performanceDeltaSort = storage.getPersistent<PerformanceDeltaSort>("performanceDeltaSort");
	const performanceDeltaSearchQuery = storage.getPersistent<string>("performanceDeltaSearchQuery");
	const evListSort = storage.getPersistent<EvListSort>("evListSort");
	const evProductType = storage.getPersistent<EvProductType | null>("evProductType");
	const evExcludedCategories = storage.getPersistent<Partial<Record<EvSetType, string[]>>>("evExcludedCategories");
	const evCategoryVariantSort = storage.getPersistent<EvCategoryVariantSort>("evCategoryVariantSort");
	const chartHistoryDuration = storage.getPersistent<ChartHistoryDuration>("chartHistoryDuration");
	const collectionSortOrder = storage.getPersistent<CollectionSortOrder>("collectionSortOrder");
	const collectionFilterQuery = storage.getPersistent<string>("collectionFilterQuery");
	const collectionSummaryTransactionSortOrder = storage.getPersistent<TransactionSummariesSortOrder>("collectionSummaryTransactionSortOrder");
	const collectionSummaryProgressSortOrder = storage.getPersistent<ProgressSummarySortOrder>("collectionSummaryProgressSortOrder");
	const transactionDetailsSortOrder = storage.getPersistent<TransactionDetailsSortOrder>("transactionDetailsSortOrder");
	const emblemSet = storage.getPersistent<EmblemSetType>("emblemSet2026");
	const publicCollectionCurrency = storage.getPersistent<Currency | null>("publicCollectionCurrency");
	const publicCollectionPriceSource = storage.getPersistent<PriceSource | null>("publicCollectionPriceSource");
	const publicTransactionSummariesSortOrder = storage.getPersistent<PublicTransactionSummariesSortOrder>("publicTransactionSummariesSortOrder");

	return {
		viewTypePreference: viewTypePreference || "List",
		performanceDeltaStep: performanceDeltaStep || "Step24Hours",
		performanceDeltaMinimum: performanceDeltaMinimum || 0,
		performanceDeltaSort: performanceDeltaSort || "PercentageGain",
		performanceDeltaSearchQuery: performanceDeltaSearchQuery || "",
		evListSort: evListSort || "OldestReleaseDate",
		evProductType: evProductType || "All",
		evExcludedCategories: evExcludedCategories || {},
		evCategoryVariantSort: evCategoryVariantSort || "HighestMarketPrice",
		chartHistoryDuration: chartHistoryDuration || "183",
		collectionSortOrder: collectionSortOrder || "Alphabetical",
		collectionFilterQuery: collectionFilterQuery || "",
		collectionSummaryTransactionSortOrder: collectionSummaryTransactionSortOrder || "Alphabetical",
		collectionSummaryProgressSortOrder: collectionSummaryProgressSortOrder || "Alphabetical",
		transactionDetailsSortOrder: transactionDetailsSortOrder || "Chronological",
		emblemSet: emblemSet || "Set2026Frontier",
		publicCollectionCurrency: publicCollectionCurrency || null,
		publicCollectionPriceSource: publicCollectionPriceSource || null,
		publicTransactionSummariesSortOrder: publicTransactionSummariesSortOrder || "Alphabetical"
	};
};

const reducer = (state: UserPreferencesState, action: UserPreferencesAction): UserPreferencesState => {
	switch(action.type) {
	case "SetViewTypePreference":
		return {
			...state,
			viewTypePreference: action.viewTypePreference
		};
	case "SetPerformanceDeltaStep":
		return {
			...state,
			performanceDeltaStep: action.step
		};
	case "SetPerformanceDeltaMinimum":
		return {
			...state,
			performanceDeltaMinimum: action.minimum
		};
	case "SetPerformanceDeltaSort":
		return {
			...state,
			performanceDeltaSort: action.sort
		};
	case "SetPerformanceDeltaSearchQuery":
		return {
			...state,
			performanceDeltaSearchQuery: action.performanceDeltaSearchQuery
		};
	case "SetEvListSort":
		return {
			...state,
			evListSort: action.sort
		};
	case "SetEvProductType":
		return {
			...state,
			evProductType: action.productType
		};
	case "SetEvExcludedCategories":
		return {
			...state,
			evExcludedCategories: action.evExcludedCategories
		};
	case "SetEvCategoryVariantSort":
		return {
			...state,
			evCategoryVariantSort: action.sort
		};
	case "SetChartHistoryDuration":
		return {
			...state,
			chartHistoryDuration: action.chartHistoryDuration
		};
	case "SetCollectionSortOrder":
		return {
			...state,
			collectionSortOrder: action.collectionSortOrder
		};
	case "SetCollectionFilterQuery":
		return {
			...state,
			collectionFilterQuery: action.collectionFilterQuery
		};
	case "SetCollectionSummaryTransactionSortOrder":
		return {
			...state,
			collectionSummaryTransactionSortOrder: action.collectionSummaryTransactionSortOrder
		};
	case "SetCollectionSummaryProgressSortOrder":
		return {
			...state,
			collectionSummaryProgressSortOrder: action.collectionSummaryProgressSortOrder
		};
	case "SetTransactionDetailsSortOrder":
		return {
			...state,
			transactionDetailsSortOrder: action.transactionDetailsSortOrder
		};
	case "SetEmblemSet":
		return {
			...state,
			emblemSet: action.emblemSet
		};
	case "SetPublicCollectionCurrency":
		return {
			...state,
			publicCollectionCurrency: action.publicCollectionCurrency
		};
	case "SetPublicCollectionPriceSource":
		return {
			...state,
			publicCollectionPriceSource: action.publicCollectionPriceSource
		};
	case "SetPublicTransactionSummariesSortOrder":
		return {
			...state,
			publicTransactionSummariesSortOrder: action.publicTransactionSummariesSortOrder
		};
	}
};

const [UserPreferencesStateProvider, useUserPreferencesStateReducer] = createGlobalReducerState(reducer, getInitialState);
export { UserPreferencesStateProvider };

export const useUserPreferencesState = () => {
	const storage = useStorage();
	const [state, dispatch] = useUserPreferencesStateReducer();

	const dispatchSetViewTypePreference = useCallback((viewTypePreference: ViewType) => {
		dispatch({
			type: "SetViewTypePreference",
			viewTypePreference
		});

		storage.setPersistent("viewTypePreference", viewTypePreference);
	}, [dispatch, storage]);

	const dispatchSetPerformanceDeltaStep = useCallback((step: PerformanceDeltaStep) => {
		dispatch({
			type: "SetPerformanceDeltaStep",
			step
		});

		storage.setPersistent("performanceDeltaStep", step);
	}, [dispatch, storage]);

	const dispatchSetPerformanceDeltaMinimum = useCallback((minimum: number) => {
		dispatch({
			type: "SetPerformanceDeltaMinimum",
			minimum
		});

		storage.setPersistent("performanceDeltaMinimum", minimum);
	}, [dispatch, storage]);

	const dispatchSetPerformanceDeltaSort = useCallback((sort: PerformanceDeltaSort) => {
		dispatch({
			type: "SetPerformanceDeltaSort",
			sort
		});

		storage.setPersistent("performanceDeltaSort", sort);
	}, [dispatch, storage]);

	const dispatchSetPerformanceDeltaSearchQuery = useCallback((performanceDeltaSearchQuery: string) => {
		dispatch({
			type: "SetPerformanceDeltaSearchQuery",
			performanceDeltaSearchQuery
		});

		storage.setPersistent("performanceDeltaSearchQuery", performanceDeltaSearchQuery);
	}, [dispatch, storage]);

	const dispatchSetEvListSort = useCallback((sort: EvListSort) => {
		dispatch({
			type: "SetEvListSort",
			sort
		});

		storage.setPersistent("evListSort", sort);
	}, [dispatch, storage]);

	const dispatchSetEvProductType = useCallback((productType: EvProductType) => {
		dispatch({
			type: "SetEvProductType",
			productType
		});

		storage.setPersistent("evProductType", productType);
	}, [dispatch, storage]);

	const dispatchSetEvExcludedCategories = useCallback((evExcludedCategories: Partial<Record<EvSetType, string[]>>) => {
		dispatch({
			type: "SetEvExcludedCategories",
			evExcludedCategories: evExcludedCategories
		});

		storage.setPersistent("evExcludedCategories", evExcludedCategories);
	}, [dispatch, storage]);

	const dispatchSetEvCategoryVariantSort = useCallback((sort: EvCategoryVariantSort) => {
		dispatch({
			type: "SetEvCategoryVariantSort",
			sort
		});

		storage.setPersistent("evCategoryVariantSort", sort);
	}, [dispatch, storage]);

	const dispatchSetChartHistoryDuration = useCallback((chartHistoryDuration: ChartHistoryDuration) => {
		dispatch({
			type: "SetChartHistoryDuration",
			chartHistoryDuration
		});

		storage.setPersistent("chartHistoryDuration", chartHistoryDuration);
	}, [dispatch, storage]);

	const dispatchSetCollectionSortOrder = useCallback((collectionSortOrder: CollectionSortOrder) => {
		dispatch({
			type: "SetCollectionSortOrder",
			collectionSortOrder
		});

		storage.setPersistent("collectionSortOrder", collectionSortOrder);
	}, [dispatch, storage]);

	const dispatchSetCollectionFilterQuery = useCallback((collectionFilterQuery: string) => {
		dispatch({
			type: "SetCollectionFilterQuery",
			collectionFilterQuery
		});

		storage.setPersistent("collectionFilterQuery", collectionFilterQuery);
	}, [dispatch, storage]);

	const dispatchSetCollectionSummaryTransactionSortOrder = useCallback((collectionSummaryTransactionSortOrder: TransactionSummariesSortOrder) => {
		dispatch({
			type: "SetCollectionSummaryTransactionSortOrder",
			collectionSummaryTransactionSortOrder
		});

		storage.setPersistent("collectionSummaryTransactionSortOrder", collectionSummaryTransactionSortOrder);
	}, [dispatch, storage]);

	const dispatchSetCollectionSummaryProgressSortOrder = useCallback((collectionSummaryProgressSortOrder: ProgressSummarySortOrder) => {
		dispatch({
			type: "SetCollectionSummaryProgressSortOrder",
			collectionSummaryProgressSortOrder
		});

		storage.setPersistent("collectionSummaryProgressSortOrder", collectionSummaryProgressSortOrder);
	}, [dispatch, storage]);

	const dispatchSetTransactionDetailsSortOrder = useCallback((transactionDetailsSortOrder: TransactionDetailsSortOrder) => {
		dispatch({
			type: "SetTransactionDetailsSortOrder",
			transactionDetailsSortOrder
		});

		storage.setPersistent("transactionDetailsSortOrder", transactionDetailsSortOrder);
	}, [dispatch, storage]);

	const dispatchSetEmblemSet = useCallback((emblemSet: EmblemSetType) => {
		dispatch({
			type: "SetEmblemSet",
			emblemSet
		});

		storage.setPersistent("emblemSet2026", emblemSet);
	}, [dispatch, storage]);

	const dispatchSetPublicCollectionCurrency = useCallback((publicCollectionCurrency: Currency) => {
		dispatch({
			type: "SetPublicCollectionCurrency",
			publicCollectionCurrency
		});

		storage.setPersistent("publicCollectionCurrency", publicCollectionCurrency);
	}, [dispatch, storage]);

	const dispatchSetPublicCollectionPriceSource = useCallback((publicCollectionPriceSource: PriceSource) => {
		dispatch({
			type: "SetPublicCollectionPriceSource",
			publicCollectionPriceSource
		});

		storage.setPersistent("publicCollectionPriceSource", publicCollectionPriceSource);
	}, [dispatch, storage]);

	const dispatchSetPublicTransactionSummariesSortOrder = useCallback((publicTransactionSummariesSortOrder: PublicTransactionSummariesSortOrder) => {
		dispatch({
			type: "SetPublicTransactionSummariesSortOrder",
			publicTransactionSummariesSortOrder
		});

		storage.setPersistent("publicTransactionSummariesSortOrder", publicTransactionSummariesSortOrder);
	}, [dispatch, storage]);

	return {
		state,
		dispatchSetViewTypePreference,
		dispatchSetPerformanceDeltaStep,
		dispatchSetPerformanceDeltaMinimum,
		dispatchSetPerformanceDeltaSort,
		dispatchSetPerformanceDeltaSearchQuery,
		dispatchSetEvListSort,
		dispatchSetEvProductType,
		dispatchSetEvExcludedCategories,
		dispatchSetEvCategoryVariantSort,
		dispatchSetChartHistoryDuration,
		dispatchSetCollectionSortOrder,
		dispatchSetCollectionFilterQuery,
		dispatchSetCollectionSummaryTransactionSortOrder,
		dispatchSetCollectionSummaryProgressSortOrder,
		dispatchSetTransactionDetailsSortOrder,
		dispatchSetEmblemSet,
		dispatchSetPublicCollectionCurrency,
		dispatchSetPublicCollectionPriceSource,
		dispatchSetPublicTransactionSummariesSortOrder
	};
};
